// FTPIG Stacking Strategy.
//
// Pairs with ftpigUnderwritingData.js. That file = the GATES
// (eligibility / age / state / H-W). This file = the STRATEGY
// (philosophy, decision tree, validated presets, compliance).
//
// USAGE
//   import { recommendStack, getPresets, STACK_STRATEGY }
//   const result = recommendStack({ age, state, ... })
//   result = { gateId, narrative, foundation[], supplements[],
//              compliance[], external[], dropped[] }
//
// NEVER hardcode these rules elsewhere — read from this object.

import { checkEligibility } from './ftpigUnderwritingData';

// Mapping from strategy product names → catalog product IDs in
// ftpigUnderwritingData.js. Anything not in the catalog (ACA,
// HPG, TriTerm) goes to STRATEGY_VIRTUAL_PRODUCTS below.
export const STRATEGY_PRODUCT_MAP = {
  'AccidentWise':              'accidentwise',
  'AccidentWise GI':           'accidentwise',
  'Accident ExpenseGuard':     'expenseguard',
  'Accident ProGuard':         'proguard',
  'Accident ProGap':           'progap',
  'CriticalGuard':             'criticalguard_combo',
  'CriticalGuard GI':          'criticalguard_combo',
  'HospitalWise Bundle 2':     'hospitalwise',
  'Hospital SafeGuard GI':     'hospital_safeguard_gi',
  'DentalWise Max':            'dentalwise',
  'DentalWise':                'dentalwise',
  'Dental Savings Complete':   'dental_savings_complete',
  'HealthiestYou':             'teladoc_health',
  'HealthiestYou (Teladoc)':   'teladoc_health',
  'AdvantageGuard':            'advantageguard',
  'Short Term Medical (STM)':  'stm',
  'STM':                       'stm',
};

// Virtual products NOT in the underwriting catalog — they require an
// external quote (ACA via HealthSherpa, HPG/TriTerm via broker portal).
// Surfaced in the recommender so the agent sees the full intended
// stack, with a "Quote externally" badge.
export const STRATEGY_VIRTUAL_PRODUCTS = {
  'ACA Marketplace': {
    id: 'aca_marketplace',
    name: 'ACA Marketplace',
    carrier: 'On-Exchange · HealthSherpa',
    category: 'major_medical',
    requiresExternalQuote: 'healthsherpa',
    mec: true,
    note: 'Only this layer satisfies MEC. Quote in HealthSherpa with your NPN attached.',
  },
  'HPG Select 2000': {
    id: 'hpg_select_2000',
    name: 'Health ProtectorGuard — Select 2000',
    carrier: 'UnitedHealthcare · Golden Rule',
    category: 'fixed_indemnity',
    requiresExternalQuote: 'broker_portal',
    mec: false,
    note: 'Fixed-indemnity foundation tier. Quote in broker portal.',
  },
  'HPG Preferred 4000': {
    id: 'hpg_preferred_4000',
    name: 'Health ProtectorGuard — Preferred 4000',
    carrier: 'UnitedHealthcare · Golden Rule',
    category: 'fixed_indemnity',
    requiresExternalQuote: 'broker_portal',
    mec: false,
  },
  'HPG Premier 5000': {
    id: 'hpg_premier_5000',
    name: 'Health ProtectorGuard — Premier 5000',
    carrier: 'UnitedHealthcare · Golden Rule',
    category: 'fixed_indemnity',
    requiresExternalQuote: 'broker_portal',
    mec: false,
  },
  'TriTerm Medical': {
    id: 'triterm_medical',
    name: 'TriTerm Medical',
    carrier: 'UnitedHealthcare · Golden Rule',
    category: 'major_medical_temp',
    requiresExternalQuote: 'broker_portal',
    mec: false,
    note: 'Up to ~3yr medically-underwritten major medical. NOT MEC.',
  },
};

// The full strategy doc, kept faithful to the source JSON.
export const STACK_STRATEGY = {
  philosophy: [
    'Sell a layered protection system, not a single plan.',
    'A foundation layer handles catastrophic/network risk; supplemental layers convert the leftover exposure (deductibles, cash flow, lump-sum shocks) into cash in the client\'s hand.',
    'Two gating variables decide the entire stack BEFORE naming a product: (1) is the client insurable, (2) what is their income/subsidy position.',
    'The primary fork is GI vs underwritten: a disqualifying condition forces guaranteed-issue products; a healthy client gets richer benefits per dollar from underwritten plans.',
    'Always disclose what is and isn\'t covered up front. Gaps build trust and prevent E&O exposure.',
  ],
  mecNote: 'Only ACA (or employer/Medicaid/Medicare) satisfies MEC. STM and TriTerm are medically underwritten major medical but are NOT MEC. Indemnity/accident/critical-illness are NOT MEC. Flag this whenever a client is in a mandate state (e.g., CA, MA, NJ, RI, DC) or asks about the ACA penalty.',
  mandateStates: ['CA', 'MA', 'NJ', 'RI', 'DC'],

  layers: [
    {
      id: 'foundation_major_medical',
      role: 'Catastrophic ceiling, network discounts, out-of-pocket max; ACA also = MEC + pre-existing coverage.',
      options: [
        { product: 'ACA Marketplace',             when: 'Subsidy-eligible, has pre-existing, or needs MEC',                 mec: true  },
        { product: 'HPG Premier 5000',            when: 'Healthy/insurable, wants fixed-indemnity foundation',              mec: false },
        { product: 'HPG Preferred 4000',          when: 'Healthy/insurable, middle tier',                                   mec: false },
        { product: 'HPG Select 2000',             when: 'Healthy/insurable, budget tier',                                   mec: false },
        { product: 'Short Term Medical (STM)',    when: 'Healthy/insurable, temporary/bridge need',                         mec: false },
        { product: 'TriTerm Medical',             when: 'Healthy/insurable, longer (~3yr) need; Copay Select Max for families with young kids', mec: false },
      ],
    },
    {
      id: 'hospital_indemnity',
      role: 'Cash per day of admission to absorb a high deductible — the deductible-killer on ACA stacks.',
      options: [
        { product: 'HospitalWise Bundle 2',  when: 'ACA/GI stacks; attaches ~80% among top producers; headline $1K/day x 10 = $10K', gi: true, note: 'Bundle 1 essentially never sold.' },
        { product: 'Hospital SafeGuard GI',  when: 'Deprioritized for healthy insurable clients',                                     gi: true },
      ],
    },
    {
      id: 'accident',
      role: 'First-dollar cash on injuries; high value for accident-exposed (truckers, self-employed, physical work, sports).',
      options: [
        { product: 'AccidentWise',            when: 'Default accident layer; $10K/$15K/$20K; no calendar-year deductible', gi: true },
        { product: 'Accident ExpenseGuard',   when: 'Wants $250 deductible product with AD&D' },
        { product: 'Accident ProGuard',       when: 'Wants accident + CI rider; SI' },
        { product: 'Accident ProGap',         when: 'Wants accident + CI + hospital admission cash' },
      ],
    },
    {
      id: 'critical_illness',
      role: 'Lump sum on cancer / heart / stroke to replace income and cover the unexpected. Cheap (~$6/mo).',
      options: [
        { product: 'CriticalGuard', when: 'Fear of a major diagnosis', note: 'IL waives high-risk/sports exclusions.' },
      ],
    },
    {
      id: 'dental_vision_hearing',
      role: 'Predictable routine costs medical doesn\'t cover.',
      options: [
        { product: 'DentalWise Max',           when: 'Wants dental + vision + hearing in one' },
        { product: 'Dental Savings Complete',  when: 'Budget option / orthodontia interest (NOT insurance — discount membership)' },
      ],
    },
    {
      id: 'telehealth',
      role: 'Cheap access, fewer office-visit frictions.',
      options: [
        { product: 'HealthiestYou (Teladoc)', when: 'Embedded in Guard/HPG plans', note: 'General medical visits unlimited at no cost; behavioral/psychiatry/derm carry per-visit fees — never misrepresent as free.' },
      ],
    },
    {
      id: 'senior_bridge',
      role: 'Post-65 / near-Medicare indemnity continuation.',
      options: [
        { product: 'AdvantageGuard', when: 'Ages 60-90; continues past Medicare eligibility as an indemnity layer' },
      ],
    },
  ],

  intakeQuestions: [
    { id: 'q_sports',         q: 'Plays sports / physically active?',                triggers: ['accident'] },
    { id: 'q_selfemp',        q: 'Self-employed / 1099 / no employer coverage?',     triggers: ['foundation_major_medical', 'accident', 'hospital_indemnity'] },
    { id: 'q_kids',           q: 'Has kids?',                                        triggers: ['foundation_major_medical', 'dental_vision_hearing'] },
    { id: 'q_healthy',        q: 'Healthy / insurable?',                             triggers: ['foundation_major_medical'] },
    { id: 'q_travels',        q: 'Travels / needs nationwide PPO access?',           triggers: ['foundation_major_medical'] },
    { id: 'q_hospital_risk',  q: 'Could survive a hospital stay financially? (No = trigger)', invert: true, triggers: ['hospital_indemnity'] },
    { id: 'q_surgery_risk',   q: 'Would one surgery bankrupt them? (Yes = trigger)', triggers: ['hospital_indemnity', 'critical_illness'] },
    { id: 'q_accident_prone', q: 'More exposed to accidents (physical work, sports)?', triggers: ['accident'] },
    { id: 'q_chronic_fear',   q: 'Fears a chronic illness (cancer / heart / stroke)?', triggers: ['critical_illness'] },
    { id: 'q_temporary',      q: 'Coverage is temporary (job change / bridge)?',     triggers: ['stm_path'] },
  ],

  decisionTree: [
    {
      step: 1, id: 'pregnancy', gate: 'pregnancy',
      condition: ({ pregnant }) => !!pregnant,
      narrative: 'STOP stacking UHC products for maternity. Referral path: Medicaid for Pregnant Women → CHIP Perinatal → ACA. Note: in TX pregnancy is NOT a QLE for mid-year ACA enrollment.',
      foundation: [], supplements: [], external: [],
      terminal: true,
      complianceRefs: ['no_existing_pregnancy'],
    },
    {
      step: 2, id: 'state_IL', gate: 'state_IL',
      condition: ({ state }) => String(state || '').toUpperCase() === 'IL',
      narrative: 'IL: STM and TriTerm unavailable. Foundation = ACA or HPG/Guard; stack = Guard/HPG + GI supplements (HospitalWise Bundle 2 + AccidentWise GI) + DentalWise Max. CriticalGuard high-risk/sports exclusions are waived in IL.',
      foundation: ['HPG Preferred 4000'],
      supplements: ['HospitalWise Bundle 2', 'AccidentWise', 'DentalWise Max'],
      external: ['ACA Marketplace'],
      complianceRefs: ['not_mec_stamp'],
    },
    {
      step: 3, id: 'near_medicare', gate: 'age_near_medicare',
      condition: ({ age }) => Number(age) >= 60,
      narrative: 'Bridge-to-65: insurable clients → STM/TriTerm bridge + AccidentWise + CriticalGuard, AdvantageGuard continues post-65. Pre-existing or subsidy-eligible → ACA primary + GI indemnity. Auto-flag at ~64.5 into the Turning-65 pipeline.',
      foundationByInsurability: {
        insurable:   ['Short Term Medical (STM)', 'TriTerm Medical'],
        gi:          ['ACA Marketplace'],
      },
      supplementsByInsurability: {
        insurable: ['AccidentWise', 'CriticalGuard', 'AdvantageGuard'],
        gi:        ['HospitalWise Bundle 2', 'AccidentWise', 'AdvantageGuard'],
      },
      complianceRefs: ['not_mec_stamp', 'turn65_pipeline'],
    },
    {
      step: 4, id: 'gi_path', gate: 'insurability',
      condition: ({ insurable }) => insurable === false,
      narrative: 'GI path. Foundation = ACA. Supplements = HospitalWise Bundle 2 + AccidentWise GI (+ CriticalGuard, DentalWise Max). This is the core ACA GI stack.',
      foundation: [],
      external: ['ACA Marketplace'],
      supplements: ['HospitalWise Bundle 2', 'AccidentWise', 'CriticalGuard', 'DentalWise Max'],
      complianceRefs: ['not_mec_stamp', 'mec_note'],
    },
    {
      step: 5, id: 'subsidy_or_mec', gate: 'subsidy_or_mec',
      condition: ({ insurable, subsidyEligible, needsMec }) => insurable !== false && (!!subsidyEligible || !!needsMec),
      narrative: 'ACA primary + supplement stack. Use ACA for MEC; supplements fill the deductible/cash gaps. Headline: $1K/day x 10 days = $10K cash to cover the ACA high deductible.',
      foundation: [],
      external: ['ACA Marketplace'],
      supplements: ['HospitalWise Bundle 2', 'AccidentWise', 'CriticalGuard'],
      complianceRefs: ['not_mec_stamp', 'mec_note'],
    },
    {
      step: 6, id: 'healthy_underwritten', gate: 'healthy_underwritten',
      condition: () => true, // catch-all
      narrative: 'Underwritten foundation: HPG tier by budget (Select 2000 / Preferred 4000 / Premier 5000), or STM/TriTerm if bridging. Add AccidentWise + CriticalGuard. Layer dental/vision/hearing per intake.',
      foundation: ['HPG Premier 5000'],
      supplements: ['AccidentWise', 'CriticalGuard'],
      complianceRefs: ['not_mec_stamp'],
    },
  ],

  clientProfiles: [
    {
      id: 'healthy_young_adult_underwritten',
      label: 'Healthy young adult (underwritten)',
      match: 'Healthy, insurable, ~30M, not subsidy-motivated',
      stack: ['HPG Premier 5000', 'AccidentWise', 'CriticalGuard'],
      monthlyEstimateUsd: 107,
      breakdown: { 'HPG Premier 5000': 86, 'AccidentWise': 15, 'CriticalGuard': 6 },
      preset: { insurable: true, subsidyEligible: false, needsMec: false, pregnant: false, age: 30, sex: 'M' },
    },
    {
      id: 'aca_gi_stack',
      label: 'ACA GI stack (subsidy or pre-existing)',
      match: 'Subsidy-eligible OR disqualifying pre-existing (seizure, diabetes, etc.)',
      stack: ['ACA Marketplace', 'HospitalWise Bundle 2', 'AccidentWise'],
      headline: '$1K/day × 10 days = $10K cash to cover the ACA high deductible',
      preset: { insurable: false, subsidyEligible: true, needsMec: true, pregnant: false, age: 45 },
    },
    {
      id: 'near_medicare_bridge_insurable',
      label: 'Near-Medicare · insurable',
      match: 'Approaching 65, insurable',
      stack: ['Short Term Medical (STM)', 'AccidentWise', 'CriticalGuard', 'AdvantageGuard'],
      preset: { insurable: true, subsidyEligible: false, needsMec: false, pregnant: false, age: 63 },
    },
    {
      id: 'near_medicare_bridge_uninsurable',
      label: 'Near-Medicare · pre-existing',
      match: 'Approaching 65, pre-existing or subsidy-eligible',
      stack: ['ACA Marketplace', 'HospitalWise Bundle 2', 'AccidentWise'],
      preset: { insurable: false, subsidyEligible: true, needsMec: true, pregnant: false, age: 63 },
    },
    {
      id: 'illinois',
      label: 'Illinois resident',
      match: 'Client in IL',
      stack: ['HPG Preferred 4000', 'HospitalWise Bundle 2', 'AccidentWise', 'DentalWise Max'],
      note: 'No STM/TriTerm in IL; CriticalGuard exclusions waived.',
      preset: { insurable: true, subsidyEligible: false, needsMec: false, pregnant: false, age: 35, state: 'IL' },
    },
  ],

  complianceRules: [
    { id: 'not_mec_stamp',          text: 'Indemnity / supplemental plans are NOT Minimum Essential Coverage (MEC). Stamp on every output.' },
    { id: 'mec_note',               text: 'STM and TriTerm are medically underwritten major medical but are NOT MEC. Clarify this for mandate-state clients (CA, MA, NJ, RI, DC).' },
    { id: 'hy_fees_disclosure',     text: 'HealthiestYou behavioral/psychiatry/derm carry per-visit fees. Never misrepresent as free.' },
    { id: 'no_existing_pregnancy',  text: 'No UHC product covers an existing pregnancy. Referral path: Medicaid for Pregnant Women → CHIP Perinatal → ACA SEP (note: pregnancy is NOT a QLE in TX).' },
    { id: 'no_orthodontia',         text: 'No UHC product covers orthodontia. Surface the referral pathway.' },
    { id: 'never_position_as_primary', text: 'Never position supplemental/indemnity as primary or ACA-equivalent.' },
    { id: 'disclose_proactively',   text: 'Disclose plan limitations proactively before close.' },
    { id: 'turn65_pipeline',        text: 'Auto-flag under-65 clients at ~64.5 into the Turning-65 pipeline.' },
  ],
};

const COMPLIANCE_BY_ID = Object.fromEntries(STACK_STRATEGY.complianceRules.map(r => [r.id, r]));

// Resolve a strategy product name to either a catalog product (with
// underwriting check) or a virtual product (external quote).
export function resolveStrategyProduct(name) {
  const catalogId = STRATEGY_PRODUCT_MAP[name];
  if (catalogId) return { kind: 'catalog', name, productId: catalogId };
  if (STRATEGY_VIRTUAL_PRODUCTS[name]) return { kind: 'virtual', name, product: STRATEGY_VIRTUAL_PRODUCTS[name] };
  return { kind: 'unknown', name };
}

// Main entry point — walk the decision tree, validate against the
// underwriting catalog, layer intake-triggered extras, return a
// structured recommendation.
export function recommendStack(input) {
  const {
    age, state, sex = 'M', height, weight,
    insurable = true, subsidyEligible = false, needsMec = false, pregnant = false,
    intakeAnswers = {},
  } = input || {};

  const ageNum = Number(age) || null;
  const stateUp = String(state || '').toUpperCase();

  // 1. Walk the decision tree — first matching gate wins.
  const gate = STACK_STRATEGY.decisionTree.find(g => {
    try { return g.condition({ ...input, age: ageNum, state: stateUp, insurable, subsidyEligible, needsMec, pregnant }); }
    catch { return false; }
  });

  if (!gate) return { gateId: null, narrative: 'No gate matched.', foundation: [], supplements: [], external: [], dropped: [], compliance: [], terminal: false };

  // 2. Pick the foundation + supplement names by gate.
  let foundationNames = gate.foundation || [];
  let supplementNames = gate.supplements || [];
  const externalNames = (gate.external || []).slice();

  if (gate.foundationByInsurability) {
    foundationNames = gate.foundationByInsurability[insurable === false ? 'gi' : 'insurable'] || [];
  }
  if (gate.supplementsByInsurability) {
    supplementNames = gate.supplementsByInsurability[insurable === false ? 'gi' : 'insurable'] || [];
  }

  // 3. Add intake-triggered layers (deduped).
  const triggeredLayerIds = new Set();
  STACK_STRATEGY.intakeQuestions.forEach(q => {
    const answer = !!intakeAnswers[q.id];
    const triggered = q.invert ? !answer : answer;
    if (triggered) (q.triggers || []).forEach(t => triggeredLayerIds.add(t));
  });
  // For each triggered layer NOT already represented, add its first option.
  triggeredLayerIds.forEach(layerId => {
    const layer = STACK_STRATEGY.layers.find(l => l.id === layerId);
    if (!layer) return;
    const firstOption = layer.options[0];
    if (!firstOption) return;
    const allNames = new Set([...foundationNames, ...supplementNames, ...externalNames]);
    if (!allNames.has(firstOption.product)) supplementNames.push(firstOption.product);
  });

  // 4. Validate catalog products against the gates. GI products are
  // never dropped for medical history; they may still drop on age/state.
  const elig = (ageNum != null || stateUp || height || weight)
    ? checkEligibility({ age: ageNum, state: stateUp, height: height ? Number(height) : null, weight: weight ? Number(weight) : null, sex })
    : null;
  const eligByProductId = elig ? Object.fromEntries(elig.map(r => [r.product.id, r])) : {};

  const resolve = (name) => {
    const r = resolveStrategyProduct(name);
    if (r.kind === 'catalog') {
      const result = eligByProductId[r.productId];
      const pass = result ? result.pass : true;
      const reasons = result ? result.reasons : [];
      return { ...r, pass, reasons, productMeta: result?.product };
    }
    return r;
  };

  const allResolved = [...foundationNames, ...supplementNames].map(resolve);
  const dropped = allResolved.filter(r => r.kind === 'catalog' && r.pass === false);
  const keepCatalog = allResolved.filter(r => r.kind === 'catalog' && r.pass !== false);
  const virtuals    = allResolved.filter(r => r.kind === 'virtual');

  // 5. External quotes (ACA, HPG, TriTerm) marked separately.
  const external = externalNames.map(resolveStrategyProduct);

  // 6. Compliance stamps: gate-specific + always-on rules.
  const stampIds = new Set(gate.complianceRefs || ['not_mec_stamp']);
  // Always stamp not_mec when supplements are present.
  if (keepCatalog.length > 0 || virtuals.some(v => v.product?.mec === false)) stampIds.add('not_mec_stamp');
  if (STACK_STRATEGY.mandateStates.includes(stateUp)) stampIds.add('mec_note');
  // If teladoc is in the stack, stamp HY fees.
  if (keepCatalog.some(r => r.productId === 'teladoc_health')) stampIds.add('hy_fees_disclosure');
  // Always require disclose_proactively.
  stampIds.add('disclose_proactively');

  const compliance = Array.from(stampIds).map(id => COMPLIANCE_BY_ID[id]).filter(Boolean);

  // Partition into foundation vs supplements based on the original names.
  const isFoundation = (name) => foundationNames.includes(name);
  return {
    gateId: gate.id,
    gateLabel: gate.gate,
    narrative: gate.narrative,
    terminal: !!gate.terminal,
    foundation:  keepCatalog.filter(r => isFoundation(r.name)).concat(virtuals.filter(v => isFoundation(v.name))),
    supplements: keepCatalog.filter(r => !isFoundation(r.name)).concat(virtuals.filter(v => !isFoundation(v.name))),
    external,
    dropped,
    compliance,
  };
}

export function getPresets() { return STACK_STRATEGY.clientProfiles; }
export function getIntakeQuestions() { return STACK_STRATEGY.intakeQuestions; }
export function getPhilosophy() { return STACK_STRATEGY.philosophy; }
export function getMecNote() { return STACK_STRATEGY.mecNote; }
